import { ShoppingCart } from "@mui/icons-material";
import { Badge, Box, IconButton, styled, type SxProps, type Theme } from "@mui/material";
import { useState } from "react";
import { type AppBarProps, Form, Layout, LayoutClasses, type LayoutProps, Sidebar, useResourceDefinitions, useStore } from "react-admin";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { sum } from "../array";
import { SearchInput } from "../components";
import { useAccount, useOrder, useWebsite } from "../hooks";
import { imageURL } from "../http";
import { AppBar } from "./app-bar";
import { Breadcrumb, contrastText } from "./breadcrumb";
import { AdminMenu } from "./menu";
import { SidebarToggleButton } from "./sidebar-toggle";

const AdminAppBar = (props: AppBarProps): JSX.Element => {
    const [text, setText] = useState("");
    const [query] = useSearchParams();
    const navigate = useNavigate();
    const { txt, ...rest } = Object.fromEntries(query);
    const [open, setOpen] = useStore("filters.open", false);
    
    const { image, name: website, accountId, message } = useWebsite();
    const [orderId] = useStore<number>("orderId");
    const { id: buyerId} = useAccount();
    
    const order = useOrder({ sellerId: accountId, buyerId, orderId  });
    const { pathname } = useLocation();
    const [_, resource, view] = pathname.split("/");
    const { name, hasCreate } = useResourceDefinitions()[resource] ?? { name: "", hasCreate: false };
    return (
        <>
            <AppBar
                {...props}
                position="fixed"
                color="primary"
                toolbar={<></>}
                userMenu={false}
                enableColorOnDark
                sx={t => ({
                    background: t.palette.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(18,18,18,0.7)",
                    backdropFilter: "blur(36px) saturate(1.35)",
                    display: "block",
                    "& .RaAppBar-toolbar": {
                        padding: 0
                    }
                })}
            >
                <>
                    <SidebarToggleButton image={image} title={website} />
                    {["", "shop", "cart", "store", "about"].includes(resource) ? (
                        <Form className="topform">
                            <SearchInput
                                sx={{
                                    height: 48,
                                    "& .MuiInputBase-root::before": {
                                        borderBottomColor: "transparent",
                                    },
                                    "& .MuiInputBase-root": {
                                        borderRadius: 0,
                                        background: "transparent",
                                    },
                                }}
                                placeholder={message}
                                label={false}
                                source="search"
                                onChange={e => setText(e.target.value)}
                                onMore={() => setOpen(!open)}
                                onKeyUp={e => {
                                    if (e.key === "Enter") {
                                        if (text) {
                                            navigate({ pathname: "/", search: new URLSearchParams({ ...rest, txt: text }).toString() }, { viewTransition: true });
                                        } else {
                                            navigate({ pathname: "/", search: new URLSearchParams(rest).toString() }, { viewTransition: true });
                                        }
                                    }
                                }}
                                onClick={() => {
                                    if (text) {
                                        navigate({ pathname: "/", search: new URLSearchParams({ ...rest, txt: text }).toString() }, { viewTransition: true });
                                    } else {
                                        navigate({ pathname: "/", search: new URLSearchParams(rest).toString() }, { viewTransition: true });
                                    }
                                }}
                            />
                        </Form>
                    ) : (
                        <>
                            <Breadcrumb name={name || resource} view={view} hasCreate={hasCreate} />
                        </>
                    )}
                    <Box sx={{ paddingRight: 0.5 }}>
                        <IconButton size="large" sx={(t: Theme) => ({ color: contrastText(t), padding: 0.5 })} aria-label="Cart" href={"/cart"}>
                            <Badge color="secondary" badgeContent={sum(order?.details ?? [], x => x.quantity)} sx={{ fontSize: "2.25rem" }}>
                                <ShoppingCart fontSize="inherit" color="primary" />
                            </Badge>
                        </IconButton>
                    </Box>
                </>
            </AppBar>
        </>
    );
};

export const AdminLayout = (props: LayoutProps): JSX.Element => {
    const { pathname } = useLocation();
    const { background } = useWebsite();
    const useBg = pathname === "/" 
    || pathname.startsWith("/shop") 
    || pathname.startsWith("/cart") 
    || pathname.startsWith("/login") 
    || pathname.startsWith("/about") 
    || pathname.startsWith("/store");
    return (
        <Layout
            {...props}
            sidebar={StyledSidebar}
            menu={AdminMenu}
            appBar={AdminAppBar}
            sx={_ => ({
                background: useBg && background ? `url(${imageURL(background)}) no-repeat center center fixed` : "",
                backgroundSize: "cover",
                [`& .${LayoutClasses.appFrame}`]: { marginTop: 0 },
                [`& .${LayoutClasses.content}`]: {
                    paddingTop: 6,
                    paddingLeft: 0,
                    paddingRight: 0,
                    backgroundColor: "transparent",
                },
            })}
        />
    );
};

const StyledSidebar = styled(Sidebar)(({ theme: t }) => ({
    height: "initial",
    "& .RaSidebar-fixed": {
        paddingTop: "60px",
        position: "relative",
    } as SxProps,
    "& .MuiPaper-root": {
        background: t.palette.mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(18,18,18,0.7)",
        backdropFilter: "blur(36px) saturate(1.35)",
    },
}));
